import * as React from "react";
import { RadioButtonIcon } from "@salt-ds/core";

import { useFormFieldProps } from "../salt/form-field-context";
import { useControlled } from "./useControlled";
import "./radio.css";

/**
 * RadioButton and RadioButtonGroup, replacing `@salt-ds/core`'s.
 *
 * ── THIS PAIR USES OUR OWN CONTEXT, UNLIKE FormField ──────────────────────────────────
 *
 * `FormFieldContext` is bridged to the vendor's object because twenty Salt inputs still read
 * it. Nothing reads the radio group context except the radio button, and BOTH are ported here
 * in the same commit — so there is no un-ported consumer to keep compatible and no reason to
 * depend on a vendor internal. Port a pair together and you own the wire between them.
 *
 * ── THE GROUP IS A <fieldset role="radiogroup">, AND THE ARIA GOES ON IT ──────────────
 *
 * When a radio sits inside a group, the FormField's label and description ids are attached to
 * the GROUP, not to each button — and each button deliberately does NOT repeat them. Repeating
 * them makes a screen reader read the field's label and helper text once per option, which on a
 * five-option question is four extra readings of the same sentence.
 *
 * That is why the button checks `radioGroup === undefined` before using the field's aria props:
 * a standalone radio still needs them, a grouped one must not have them.
 *
 * ── SELECTION LIVES IN THE GROUP WHEN THERE IS ONE ────────────────────────────────────
 *
 * A radio is only meaningful against its siblings, so the group owns `value` and each button
 * derives `checked` from it. Left to themselves, two buttons can both be checked — which the
 * DOM permits whenever they do not share a `name`, and the group is what supplies that name.
 */
export type RadioValidationStatus = "error" | "warning" | "success";

interface RadioGroupContextValue {
  disabled?: boolean;
  name?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  validationStatus?: RadioValidationStatus;
  value?: string;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | undefined>(undefined);

function join(...values: (string | undefined)[]): string | undefined {
  return values.filter(Boolean).join(" ") || undefined;
}

export interface RadioButtonProps
  extends Omit<
    React.InputHTMLAttributes<HTMLLabelElement>,
    "onChange" | "onBlur" | "onFocus" | "value" | "checked"
  > {
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: React.ReactNode;
  name?: string;
  readOnly?: boolean;
  validationStatus?: RadioValidationStatus;
  value?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

export const RadioButton = React.forwardRef<HTMLLabelElement, RadioButtonProps>(
  function RadioButton(
    {
      checked: checkedProp,
      className,
      disabled: disabledProp,
      error,
      inputProps = {},
      inputRef,
      label,
      name: nameProp,
      onBlur,
      onChange,
      onFocus,
      readOnly: readOnlyProp,
      validationStatus: validationStatusProp,
      value,
      ...rest
    },
    ref,
  ) {
    const {
      a11yProps: formFieldA11yProps,
      disabled: formFieldDisabled,
      readOnly: formFieldReadOnly,
      validationStatus: formFieldValidationStatus,
    } = useFormFieldProps();
    const radioGroup = React.useContext(RadioGroupContext);

    const {
      "aria-describedby": inputDescribedBy,
      "aria-labelledby": inputLabelledBy,
      className: inputClassName,
      onChange: inputOnChange,
      ...restInputProps
    } = inputProps;

    const disabled = radioGroup?.disabled || formFieldDisabled || disabledProp;
    const readOnly = radioGroup?.readOnly || formFieldReadOnly || readOnlyProp;
    const validationStatus = !disabled
      ? (radioGroup?.validationStatus ?? formFieldValidationStatus ?? validationStatusProp)
      : undefined;

    // The group owns selection when there is one; a lone radio falls back to its own prop.
    const radioGroupChecked =
      checkedProp ?? (radioGroup && value ? radioGroup.value === value : checkedProp);
    const name = nameProp ?? radioGroup?.name;

    const [checked, setCheckedState] = useControlled<boolean>({
      controlled: radioGroupChecked,
      default: Boolean(checkedProp),
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // Same reason as Checkbox: a native radio ignores the `readonly` attribute, so read-only
      // has to be enforced by refusing the change.
      if (readOnly) return;
      setCheckedState(event.target.checked);
      onChange?.(event);
      inputOnChange?.(event);
      radioGroup?.onChange?.(event);
    };

    const classes = ["saltRadioButton"];
    if (disabled) classes.push("saltRadioButton-disabled");
    if (readOnly) classes.push("saltRadioButton-readOnly");
    if (error) classes.push("saltRadioButton-error");
    if (validationStatus) classes.push(`saltRadioButton-${validationStatus}`);
    if (className) classes.push(className);

    return (
      <label className={classes.join(" ")} ref={ref} {...rest}>
        <input
          // Inside a group the field's ids belong to the fieldset, NOT to each option —
          // repeating them reads the label and helper text once per radio.
          aria-describedby={join(
            radioGroup === undefined ? formFieldA11yProps?.["aria-describedby"] : undefined,
            inputDescribedBy,
          )}
          aria-labelledby={join(
            radioGroup === undefined ? formFieldA11yProps?.["aria-labelledby"] : undefined,
            inputLabelledBy,
          )}
          checked={checked}
          className={
            inputClassName ? `saltRadioButton-input ${inputClassName}` : "saltRadioButton-input"
          }
          disabled={disabled}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={onFocus}
          readOnly={readOnly}
          ref={inputRef}
          type="radio"
          value={value}
          {...restInputProps}
        />
        <RadioButtonIcon
          checked={checked}
          error={error}
          readOnly={readOnly}
          validationStatus={validationStatus}
        />
        {/* A bare child, as with Checkbox — a wrapper span is a flex child nothing styles. */}
        {label}
      </label>
    );
  },
);

export interface RadioButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLFieldSetElement>, "onChange" | "defaultValue"> {
  defaultValue?: string;
  direction?: "horizontal" | "vertical";
  disabled?: boolean;
  name?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  validationStatus?: RadioValidationStatus;
  value?: string;
  wrap?: boolean;
}

export const RadioButtonGroup = React.forwardRef<HTMLFieldSetElement, RadioButtonGroupProps>(
  function RadioButtonGroup(
    {
      "aria-describedby": ariaDescribedBy,
      "aria-labelledby": ariaLabelledBy,
      children,
      className,
      defaultValue,
      direction = "vertical",
      disabled: disabledProp,
      name: nameProp,
      onChange,
      readOnly: readOnlyProp,
      validationStatus: validationStatusProp,
      value: valueProp,
      wrap = true,
      ...rest
    },
    ref,
  ) {
    const {
      a11yProps,
      disabled: formFieldDisabled,
      readOnly: formFieldReadOnly,
      validationStatus: formFieldValidationStatus,
    } = useFormFieldProps();

    const disabled = formFieldDisabled || disabledProp;
    const readOnly = formFieldReadOnly || readOnlyProp;
    const validationStatus = formFieldValidationStatus ?? validationStatusProp;

    const [value, setStateValue] = useControlled<string | undefined>({
      controlled: valueProp,
      default: defaultValue,
    });

    const generatedName = React.useId();
    const name = nameProp ?? generatedName;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setStateValue(event.target.value);
      onChange?.(event);
    };

    const classes = ["saltRadioButtonGroup", `saltRadioButtonGroup-${direction}`];
    if (!wrap) classes.push("saltRadioButtonGroup-noWrap");
    if (className) classes.push(className);

    /*
     * DELIBERATELY NOT MEMOISED, and this cost a real bug.
     *
     * Memoising it looked free: a context value is an object, a fresh one re-renders every
     * consumer, so pin it. But `handleChange` closes over the caller's `onChange`, which closes
     * over the page's current state. Memoising on [disabled, name, readOnly, validationStatus,
     * value] froze `onChange` at its first-render identity, so every later answer was written
     * against a STALE draft — the first answer was lost and the form could never be completed.
     *
     * It surfaced as `card-acceptance-workspace.test.tsx` failing with "expected submit to be
     * called 1 times, but got 0": both radios fired with the right values, the draft was simply
     * missing one of them. No type error, no render error, nothing visible in the DOM.
     *
     * The vendor builds this object inline on every render, and that is correct. The cost is a
     * re-render of a handful of radios; the alternative was silent data loss.
     */
    const contextValue = {
      disabled,
      name,
      onChange: handleChange,
      readOnly,
      validationStatus,
      value,
    };

    return (
      <fieldset
        aria-describedby={join(a11yProps?.["aria-describedby"], ariaDescribedBy)}
        aria-labelledby={join(a11yProps?.["aria-labelledby"], ariaLabelledBy)}
        aria-readonly={readOnly}
        className={classes.join(" ")}
        data-testid="radio-button-group"
        ref={ref}
        // A fieldset alone is a "group"; radiogroup is what makes a screen reader announce
        // "option 2 of 5" as the user arrows through.
        role="radiogroup"
        {...rest}
      >
        <RadioGroupContext.Provider value={contextValue}>{children}</RadioGroupContext.Provider>
      </fieldset>
    );
  },
);
